import {
    AryaAdaptor,
    AryaEvent,
    AryaFurnitureSummary,
    AryaRoom,
    Mode,
} from '@digital-bridge/artisan-arya';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import * as Hooks from 'preact/hooks';
import { AnyAction, Dispatch } from 'redux';
import {
    FurnitureDescription,
    articlesFromFurnitureDescription,
} from '../../data/furnitureDescription';
import { Summary } from '../../data/summary';
import * as Su from '../../data/summary';
import * as Sofa from '../../data/modaSofa';
import { Pose } from '../../data/pose';
import * as S from '../../store';
import * as CoreStore from '../../store/core';
import * as FurnitureListStore from '../../store/furnitureList';
import * as Selectors from '../../store/selectors';
import { useDispatch } from '../hooks/redux';
import * as FurniturePlacement from './data/furniturePlacement';
import * as Scene from './data/scene';

/**
 * The Arya renderer is controlled imperatively, but the Application's Redux State is
 * intended to be a single declarative source of truth.
 *
 * The purpose of this interface is to deal with this friction by keeping the Application data in sync
 * with the Arya internals (e.g. when imperatively instructing Arya to render something, simultaneously
 *  inform the Redux State that the scene has been updated).
 *
 * For this reason, all interaction with Arya inside dora should happen via this interface, otherwise dora
 *  would fall out of sync.
 *
 * Note: the responsibility of making the actual Arya calls is handled by the Scene type (see ./data/scene)
 */
export interface Interface {
    addFurniture(
        fd: FurnitureDescription,
        summary: Summary,
        reason: 'load_layout' | 'user_add_item' | 'user_change_colour',
        pose?: Pose,
        autoDisplay?: boolean,
    ): Promise<string | undefined>;

    /** takes the persistent furniture ID */
    removeFurniture(fid: string): void;
    /** delete every element in a furniture group */
    removeFurnitureGroup(groupId: string): void;

    /** Returns an array of persistent furniture IDs  */
    listFurniture(): Array<string>;
    /** Returns some very basic furniture information (crucially the "space" id, which
     * is used to pass-in and retrieve the sku of non-modular products for layout
     * serialisation). This was previously used in Aspect to populate the basket from
     * the details in a configuration space document. */
    furnitureSummaries(): Array<AryaFurnitureSummary>;

    setFloorCovering(id: string): void;

    /** Generate furniture models for all models (no room) in the scene */
    generateFurnitureGLTF(): Promise<ArrayBuffer>;
    generateFurnitureUSDZ(): Promise<ArrayBuffer>;
    on(event: AryaEvent, cb: (data: unknown) => void): void;
    off(event: AryaEvent, cb: (data: unknown) => void): void;
    selectFurniture(fid: string): void;
    selectedFurniture(): Array<string>;
    furniturePose(furnitureId: string): O.Option<Pose>;
    furnitureProducts(furnitureId: string): O.Option<Array<string>>;
    configurationTemplateId(furnitureId: string): O.Option<string>;

    /** Arya considers a furniture to be ready when its bounding box has been constructed.
     * At this point the furniture entity is complete, and ready to be handled by Dora.
     * For simple furniture this is positioning and un-hiding the furniture, but modular furniture
     * requires extra steps to group the constituent entities before they can be positioned and
     * un-hidden in the scene.
     */
    furnitureReady(fid: string): O.Option<boolean>;
    updateRoomDimensions(room: AryaRoom): Promise<void>;
    roomDimensions(): AryaRoom;
    addModularSofa(
        configuration: Sofa.ModaSofa.Configuration,
        pose: Pose,
        range: string,
        trigger: string,
    ): Promise<string | undefined>;
    removeModularSofa(sofaId: string): void;
    clearScene(): void;
    /**  Set the scene camera back to its initial position */
    resetCamera(): void;

    /** Switch to focus mode */
    focusOnFurniture(fid: string): void;
    updateFurniture(fid: string, furnitureDescription: FurnitureDescription): void;
    toggleViewMode(mode: Mode): void;
}

const fromScene = (scene: Scene.Scene, dispatch: Dispatch<AnyAction>): Interface => {
    return {
        /**
         * Add a single piece of Furniture to the scene. Also informs the Redux FurnitureList store that furniture has been added
         *
         * TODO: Consider exposing a specific method for each 'reason' here - they each have slightly different requirements currently
         *       handled at their call-sites which could be handled in this interface.
         */
        async addFurniture(
            fd: FurnitureDescription,
            summary: Summary,
            reason: 'load_layout' | 'user_add_item' | 'user_change_colour',
            pose?: Pose,
            autoDisplay: boolean = true,
        ) {
            return await pipe(scene, Scene.addFurniture(fd, pose, autoDisplay)).then(
                furnitureId => {
                    if (furnitureId) {
                        dispatch(
                            FurnitureListStore.addFurniture({
                                aryaId: furnitureId,
                                productIds: fd.variants.map(p => p.canonical_id),
                                articleTypes: articlesFromFurnitureDescription(fd),
                                configuration: O.none,
                                summary,
                                trigger: reason,
                            }),
                        );
                    }

                    return furnitureId;
                },
            );
        },

        async updateRoomDimensions(_room: AryaRoom) {
            const room = pipe(scene, Scene.updateRoomDimensions(_room));
            dispatch(CoreStore.setSpaceRoom(room));
        },
        roomDimensions(): AryaRoom {
            return pipe(scene, Scene.roomDimensions);
        },
        removeFurniture(furnitureId: string) {
            return pipe(scene, Scene.removeFurniture(furnitureId));
        },
        removeFurnitureGroup(groupId: string) {
            pipe(scene, Scene.removeFurnitureGroup(groupId));
        },
        listFurniture() {
            return pipe(scene, Scene.listFurniture);
        },
        furnitureSummaries() {
            return pipe(scene, Scene.furnitureSummaries);
        },
        setFloorCovering(id: 'paving-new') {
            pipe(scene, Scene.setFloorCovering(id));
        },
        selectFurniture(furnitureId: string) {
            return pipe(scene, Scene.selectFurniture(furnitureId));
        },
        selectedFurniture() {
            return pipe(scene, Scene.selectedFurniture);
        },
        furniturePose(furnitureId: string): O.Option<Pose> {
            return pipe(scene, Scene.furniturePose(furnitureId));
        },
        furnitureProducts(furnitureId: string) {
            return pipe(scene, Scene.furnitureProducts(furnitureId));
        },
        configurationTemplateId(furnitureId: string) {
            return pipe(scene, Scene.configurationTemplateId(furnitureId));
        },
        furnitureReady(fid: string) {
            return pipe(scene, Scene.furnitureReady(fid));
        },
        generateFurnitureGLTF() {
            return Scene.generateFurnitureGltf(scene);
        },
        generateFurnitureUSDZ() {
            return Scene.generateFurnitureUsdz(scene);
        },

        /**  Handle any AryaEvent */
        on(event: AryaEvent, cb: (data: unknown) => void) {
            pipe(scene, Scene.onEvent(event, cb));
        },
        off(event: AryaEvent, cb: (data: unknown) => void) {
            pipe(scene, Scene.offEvent(event, cb));
        },

        /** Add a modular sofa to both Arya and the Furniture List */
        async addModularSofa(
            config: Sofa.ModaSofa.Configuration,
            pose: Pose,
            range: string,
            /** Indicates the reason for adding a modular sofa */
            trigger: string,
        ): Promise<string> {
            // Fetching the Correct templates using the config
            const furnitureBySku = Selectors.Catalogue.simpleFurnitureDescriptionBySku(
                S.store.getState(),
            );
            return pipe(
                E.Do,
                E.bind('template', () => Sofa.templateFromConfig(config)),
                E.bind('pieces', ({ template }) =>
                    E.tryCatch(
                        () =>
                            Sofa.ModaSofa.placePieces(template)(
                                config.size,
                                config.interstitialPlacements,
                                config.cornerStyle,
                            ),
                        _ => 'Unable to place pieces for unknown reason',
                    ),
                ),
                E.bind('furnitureToAdd', ({ pieces }) =>
                    FurniturePlacement.fromPieces(pieces, furnitureBySku),
                ),
                // For some reason, TE.fromEither doesn't work in this position, returning any. Manually converting into a TE instead
                E.match(TE.left, x => TE.right(x)),
                TE.bind('sofaId', ({ furnitureToAdd }) =>
                    TE.tryCatch(
                        () => pipe(scene, Scene.addFurnitureGroup(furnitureToAdd, pose)),
                        err => 'Unable to construct furniture group',
                    ),
                ),
                TE.chain(({ sofaId, template, pieces }) => {
                    dispatch(
                        FurnitureListStore.addFurniture({
                            aryaId: sofaId,
                            productIds: pieces
                                .filter(p => p.kind !== 'sofa-end-right')
                                .map(p => p.sku),
                            configuration: O.some(config),
                            articleTypes: ['modularSofa'],
                            summary: Sofa.summary(template, config, range ?? ''),
                            trigger,
                        }),
                    );
                    return TE.right(sofaId);
                }),
                TE.match(Promise.reject, sofaId => Promise.resolve(sofaId)),
            )();
        },

        /**  Remove a sofa from Arya & the Furniture List */
        removeModularSofa(sofaId: string): void {
            this.removeFurnitureGroup(sofaId);
            dispatch(FurnitureListStore.removeFurnitureByAryaId(sofaId));
        },

        /**
         * Clear the scene
         */
        clearScene(): void {
            const furnitureList = Selectors.FurnitureList.furnitureList(S.store.getState());

            furnitureList.forEach(({ aryaId, configuration }) => {
                O.isSome(configuration)
                    ? this.removeModularSofa(aryaId)
                    : this.removeFurniture(aryaId);
            });
        },

        resetCamera(): void {
            pipe(scene, Scene.resetCamera);
        },
        focusOnFurniture(fid: string): void {
            pipe(scene, Scene.focusOnFurniture(fid));
        },
        updateFurniture(fid: string, fd: FurnitureDescription): void {
            pipe(scene, Scene.updateFurniture(fid, fd));
        },
        toggleViewMode(mode: Mode): void {
            pipe(scene, Scene.setViewMode(mode));
        },
    };
};

/**
 *
 * construct an Arya Interface
 *
 * NOTE: This hook is a special case - due to how it is used elsewhere, the interface should not be
 * expected to have access to the most up to date data found by selectors. Ideally, data from the
 * store should be passed to functions in the Interface returned. The interface requires a
 * surrounding hook in order to dispatch actions.
 *
 * For this reason, the methods on this interface also access the state of the store directly to
 * avoid hooks. This should only occur in places like this interface, where direct use of hooks is
 * impractical.
 */
export const build = (arya: AryaAdaptor | null): O.Option<Interface> => {
    const dispatch = useDispatch();

    return Hooks.useMemo(() => {
        if (arya !== null) {
            // passing dispatch directly here rather than calling `useDispatch` in `fromScene` to avoid breaking the laws of hooks
            // this function is internal to this file, so this awkwardness shouldn't be a huge issue.
            return O.some(fromScene(arya, dispatch));
        } else {
            return O.none;
        }
    }, [arya, dispatch]);
};
